import { Button, Layout, Space, Tree, Tooltip, message } from 'antd';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import { debounce } from 'lodash';
import { useRequest } from 'ahooks';

import { DownloadOutlined, LeftOutlined, RightOutlined, SaveOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { convertToTreeStructure } from '../store';

import { SmileOutlined } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import { Result } from 'antd';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { get, post } from '../../../axios/axios';
import { useDevice } from '../../../hooks/useDevice';
import { DownloadPaperModal } from '../Step3/components/DownloadPaperModal';
import AcademicEditor from './components/AcademicEditor/AcademicEditor';
import RepetitionModal from './components/Modals/RepetitionModal';
import ExpansionModal from './components/Modals/ExpansionModal';
import ImgModal from './components/Modals/ImgModal';
import LessenModal from './components/Modals/LessenModal';
import RewriteModal from './components/Modals/RewriteModal';
import TableModal from './components/Modals/TableModal';
import repetition from './images/icon_repetition.svg';
import expansion from './images/icon_expansion.svg';
import img from './images/icon_img.svg';
import lessen from './images/icon_lessen.svg';
import rewrite from './images/icon_rewrite.svg';
import chart from './images/icon_chart.svg';
const { Sider, Content } = Layout;

export const defaultOrderNo = 'a242a7d8e4004a37a4070719705b765f';
const ConditionalWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

export const Editor = observer(() => {
    const editorRef = useRef(null);

    const location = useLocation();
    const [qs] = useSearchParams();
    const [catalogueData, setCatalogueData] = useState('');
    const [catalogueDataLoaded, setCatalogueDataLoaded] = useState(false);
    // 获取编辑器大纲和目录
    const getCatalogueData = async () => {
        try {
            const res = await get('/ai/ai/paper/editor/get/outline', {
                orderNo: qs.get('orderNo'),
            });

            setCatalogueData(convertToTreeStructure(res.data));
            setCatalogueDataLoaded(true);
        } catch (e) {
            setCatalogueDataLoaded(false);
        }
    };
    const [abstractDataInceptionData, setAbstractDataInceptionData] = useState({});
    // 获取编辑器摘要
    const getAbstractData = async () => {
        try {
            const res = await get('/ai/ai/paper/editor/get/abstract', {
                orderNo: qs.get('orderNo'),
            });

            setAbstractDataInceptionData(res.data);
        } catch (e) {}
    };
    const [contentDataLoaded, setContentDataLoaded] = useState(false);
    const [contentInceptionData, setContentInceptionData] = useState({});
    // 获取编辑器内容
    const getContentData = async () => {
        try {
            setContentDataLoaded(false);
            const res = await get('/ai/ai/paper/editor/get/content', {
                orderNo: qs.get('orderNo'),
            });

            setContentInceptionData(res.data);
            setContentDataLoaded(true);
        } catch (e) {
            setContentDataLoaded(false);
        }
    };
    const isDefaultOrderNo = useMemo(() => {
        return qs.get('orderNo') === defaultOrderNo;
    }, [qs]);

    // 默认订单禁用编辑器
    const setEditorDisabled = useCallback(() => {
        editorRef.current.setEditable(false);
    }, []);

    useEffect(() => {
        if (isDefaultOrderNo && contentDataLoaded) {
            setEditorDisabled();
        }
    }, [contentDataLoaded]);

    useEffect(() => {
        getCatalogueData();
        getAbstractData();
        getContentData();
    }, [location.search]);

    const siderStyle = {
        overflow: 'auto',
        height: 'calc(100vh - 56px)',
        position: 'fixed',
        top: 0,
        bottom: 0,
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
        backgroundColor: '#fff',
    };

    const [collapsed, setCollapsed] = useState(false);

    const toggleCollapsed = () => {
        setCollapsed(!collapsed);
    };
    // 展开/收起按钮样式
    const toggleButtonStyle = {
        position: 'absolute',
        right: '-16px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '32px',
        height: '32px',
        backgroundColor: '#fff',
        border: '1px solid #EEEEF1',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    };

    // 一键降重
    const useRepetitionModal = useModal(RepetitionModal);
    // 章节扩写
    const useExpansionModal = useModal(ExpansionModal);
    // 章节缩写
    const useLessenModal = useModal(LessenModal);
    // 重写章节
    const useRewriteModal = useModal(RewriteModal);
    // 修改图片
    const useImgModal = useModal(ImgModal);
    // 修改表格
    const useTableModal = useModal(TableModal);

    const Tools = useMemo(() => {
        return [
            {
                id: 'repetition',
                title: '降重',
                icon: repetition,
                modal: useRepetitionModal,
            },
            {
                id: 'expansion',
                title: '扩写',
                icon: expansion,
                modal: useExpansionModal,
            },
            { id: 'lessen', title: '缩写', icon: lessen, modal: useLessenModal },
            {
                id: 'rewrite',
                title: '重写章节',
                icon: rewrite,
                modal: useRewriteModal,
            },
            { id: 'image', title: '修图', icon: img, modal: useImgModal },
            { id: 'table', title: '修表', icon: chart, modal: useTableModal },
        ];
    }, []);

    const handleToolClick = useCallback((e, item) => {
        e.preventDefault(); // 阻止默认行为
        e.stopPropagation(); // 阻止事件冒泡
        clickTool(item);
    }, []);

    const clickTool = useCallback((item) => {
        editorRef.current.handleButtonClick(item.id);
    }, []);

    // 当前是否有modal打开
    const [isOpeningModal, setIsOpeningModal] = useState(false);
    const mlVal = useMemo(() => {
        // 如果弹窗打开，偏移量加大
        if (isOpeningModal) {
            return '-36%';
        }
        // 如果只有collapsed，则偏移
        return collapsed ? 0 : '12%';
    }, [collapsed, isOpeningModal]);

    // 关闭modal
    const handleCloseModal = () => {
        setCollapsed(false);
        setIsOpeningModal(false);
    };

    // 使用useRequest轮询查询论文状态
    const {
        data: statusData,
        run: checkStatus,
        cancel: stopCheckStatus,
    } = useRequest(
        async (orderNo) => {
            if (!orderNo || orderNo === defaultOrderNo) return { data: 0 };

            try {
                const res = await post('/ai/ai/paper/editor/queryStatus', {
                    orderNo,
                });
                console.log('查询论文状态:', res);
                return res;
            } catch (error) {
                console.error('查询状态失败:', error);
                return { data: 0 }; // 出错时默认继续编辑
            }
        },
        {
            pollingInterval: 10000, // 每10秒轮询一次
            pollingWhenHidden: false, // 页面隐藏时不轮询
            manual: true, // 手动启动轮询
            ready: !isDefaultOrderNo, // 只有非默认订单才启用轮询
            onSuccess: (result) => {
                if (result && result.data === 1) {
                    message.warning('该论文已被系统锁定，即将返回首页');
                    stopCheckStatus();
                    stopAutoSave();
                    setTimeout(() => {
                        navigate('/');
                    }, 2000);
                }
            },
        },
    );

    // 使用useRequest处理自动保存
    const { run: saveContent, cancel: stopAutoSave } = useRequest(
        async (content, orderNo) => {
            if (!content || !orderNo) {
                return Promise.reject('内容或订单号不能为空');
            }

            // 先检查状态，如果状态为1则不保存
            if (statusData && statusData.data === 1) {
                return Promise.reject('论文已锁定，无法保存');
            }

            const res = await post('/ai/ai/paper/editor/saveFullContent', {
                orderNo,
                content,
            });

            if (res.code !== 0) {
                return Promise.reject(res.message || '保存失败');
            }

            return res.data;
        },
        {
            manual: true,
            debounceWait: 2000, // 2秒防抖
            onSuccess: () => {
                console.log('自动保存成功:', new Date().toLocaleTimeString());
            },
            onError: (error) => {
                console.error('自动保存失败:', error);
            },
        },
    );

    // 自动保存处理函数
    const handleAutoSaveContent = useCallback(() => {
        console.log('尝试自动保存，时间:', new Date().toLocaleTimeString());

        // 检查编辑器引用
        if (!editorRef?.current) {
            console.log('跳过自动保存: 编辑器不可用');
            return;
        }

        // 如果论文已锁定，不执行保存
        if (statusData && statusData.data === 1) {
            console.log('跳过自动保存: 论文已锁定');
            return;
        }

        // 获取内容
        const content = editorRef.current.getContent();
        if (!content) {
            console.log('跳过自动保存: 无内容');
            return;
        }

        // 获取订单号并调用保存
        const orderNo = qs.get('orderNo');
        saveContent(content, orderNo);
    }, [qs, saveContent, statusData]);

    // 启动状态检查和自动保存
    useEffect(() => {
        // 如果是默认订单号，不启动轮询和自动保存
        if (isDefaultOrderNo) {
            return;
        }

        // 开始轮询检查状态（只需启动一次）
        const orderNo = qs.get('orderNo');
        checkStatus(orderNo);

        // 设置自动保存的定时器 (每30秒触发一次)
        const autoSaveTimer = setInterval(() => {
            // 只有当状态正常(0)时才执行自动保存
            if (!statusData || statusData.data === 0) {
                handleAutoSaveContent();
            }
        }, 60000);

        // 清理定时器和轮询
        return () => {
            clearInterval(autoSaveTimer);
            stopCheckStatus();
            stopAutoSave();
        };
    }, [isDefaultOrderNo, qs]); // 减少依赖项，只有在订单号变化时才重新设置

    // 优化手动保存函数
    const handleSaveContent = async () => {
        if (!editorRef?.current) {
            console.warn('编辑器引用不可用');
            return;
        }

        // 如果论文已锁定，提示用户并返回
        if (statusData && statusData.data === 1) {
            message.warning('该论文已被系统锁定，无法保存');
            return;
        }

        // 获取编辑器实时内容
        const content = editorRef.current.getContent();

        if (content) {
            try {
                message.loading('正在保存...');

                const orderNo = qs.get('orderNo');
                // 调用保存API
                const res = await post('/ai/ai/paper/editor/saveFullContent', {
                    orderNo,
                    content,
                });

                if (res.code === 0) {
                    message.success('保存成功');
                } else {
                    message.error(res.message || '保存失败');
                }
            } catch (error) {
                console.error('保存失败:', error);
                message.error('保存失败，请重试');
            }
        }
    };

    // 保存单章
    const onSaveSingChapter = async ({ chapterId, chapterContent, version }) => {
        const res = await post('/ai/ai/paper/editor/save', {
            orderNo: qs.get('orderNo'),
            chapterId,
            chapterContent,
            version,
        });
        if (res.code === 0) {
            console.log('保存单章失败');
        } else {
            message.error(res.message || '保存失败');
        }
    };

    // 根据type显示不同的modal
    const handleOpenModal = ({ type, content, from, to, chapterId, id = '' }) => {
        setCollapsed(true);
        setIsOpeningModal(true);

        switch (type) {
            case 'repetition':
                useRepetitionModal
                    .show({
                        content,
                        orderNo: qs.get('orderNo'),
                        from,
                        to,
                        chapterId,
                    })
                    .then((newContent) => {
                        handleCloseModal();
                        // 更新内容以后,会自动保存单章
                        editorRef.current.updateTextContent(from, to, newContent);
                    })
                    .catch(() => {
                        handleCloseModal();
                    });
                break;
            case 'expand':
                useExpansionModal
                    .show({
                        content,
                        orderNo: qs.get('orderNo'),
                        from,
                        to,
                        chapterId,
                    })
                    .then((newContent) => {
                        handleCloseModal();
                        // 更新内容以后,会自动保存单章
                        editorRef.current.updateTextContent(from, to, newContent);
                    })
                    .catch(() => {
                        handleCloseModal();
                    });
                break;
            case 'shrink':
            case 'lessen':
                useLessenModal
                    .show({
                        content,
                        orderNo: qs.get('orderNo'),
                        from,
                        to,
                        chapterId,
                    })
                    .then((newContent) => {
                        handleCloseModal();
                        // 更新内容以后,会自动保存单章
                        editorRef.current.updateTextContent(from, to, newContent);
                    })
                    .catch(() => {
                        handleCloseModal();
                    });
                break;
            case 'rewrite':
                useRewriteModal
                    .show({
                        content,
                        orderNo: qs.get('orderNo'),
                        from,
                        to,
                        chapterId,
                    })
                    .then(async (newChapterContent) => {
                        handleCloseModal();
                        console.log('newChapterContent', newChapterContent);
                        // 更新内容以后会自动进行更新
                        editorRef.current.updateTextContent(from, to, newChapterContent);
                    })
                    .catch(() => {
                        handleCloseModal();
                    });
                break;
            case 'image':
                useImgModal
                    .show({
                        content,
                        orderNo: qs.get('orderNo'),
                        from,
                        to,
                        chapterId,
                        id,
                    })
                    .then((newSrc) => {
                        handleCloseModal();
                        // 更新内容以后会自动进行更新
                        editorRef.current.updateImageSrc(id, newSrc);
                    })
                    .catch(() => {
                        handleCloseModal();
                    });
                break;
            case 'table':
                useTableModal
                    .show({
                        content,
                        id,
                        orderNo: qs.get('orderNo'),
                        from,
                        to,
                        chapterId,
                    })
                    .then(async (result) => {
                        handleCloseModal();
                        editorRef.current.updateTableHtml(id, from, to, result.content);
                        console.log(id, from, to);

                        //                         editorRef.current.updateTableHtml(
                        //                             id,
                        //                             from,
                        //                             to,
                        //                             `[AIML_TABLE id=1231232132131321]| 指令类型 | 功能描述 |
                        // | --- | --- |
                        // | 算术指令1 | 执行加、减、乘、除等基本算术运算 |
                        // | 逻辑指令2 | 执行与、或、非等逻辑运算 |
                        // | 数据传送指令 | 在寄存器、内存和输入输出设备之间传送数据 |
                        // | 控制指令 | 改变程序执行的顺序，如跳转、调用和返回 |
                        // | 输入输出指令 | 与外部设备进行数据交换 |[/AIML_TABLE]`,
                        //                         );
                    })
                    .catch(() => {
                        handleCloseModal();
                    });
                break;
            default:
                break;
        }
    };

    const [searchParams] = useSearchParams();
    const useDownloadPaperModal = useModal(DownloadPaperModal);
    // 进行全文下载
    const showOrderModal = () => {
        const orderNo = searchParams.get('orderNo');
        useDownloadPaperModal.show({
            orderNo,
            queryType: 'query2',
        });
    };

    const [wordCount, setWordCount] = useState(0);
    const getWordCount = useCallback((editorWordCount) => {
        setWordCount(editorWordCount);
    }, []);

    const { isMobile } = useDevice();

    const navigate = useNavigate();

    return (
        <>
            {isMobile ? (
                <div className="flex h-screen w-screen items-center justify-center">
                    <Result
                        icon={<SmileOutlined />}
                        title="为了获得更好的使用体验，请在PC端使用"
                        extra={
                            <Button type="primary" onClick={() => navigate('/')}>
                                返回首页
                            </Button>
                        }
                    />
                </div>
            ) : (
                <Layout
                    style={{
                        backgroundColor: '#fff',
                        marginTop: 12,
                        position: 'relative',
                    }}
                >
                    <Sider
                        width={'18%'}
                        collapsible
                        collapsed={collapsed}
                        trigger={null}
                        collapsedWidth={0}
                        style={{
                            ...siderStyle,
                            left: 0,
                            top: 56,
                            padding: 12,
                            borderRight: '1px solid #EEEEF1',
                            overflow: 'initial',
                            '&::WebkitScrollbar': {
                                width: '0px',
                            },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                        }}
                    >
                        <div
                            className={'overflow-y-scroll'}
                            style={{ height: 'calc(100% - 32px)' }}
                        >
                            {catalogueDataLoaded && !collapsed ? (
                                <Tree
                                    defaultExpandAll={catalogueDataLoaded}
                                    showLine={false}
                                    showIcon={false}
                                    treeData={catalogueData}
                                    key={'id'}
                                    onSelect={(selectedKeys, info) => {
                                        console.log('selectedKeys', selectedKeys);
                                        console.log('info', info);
                                        editorRef.current.scrollToHeadingText(info.node.title);
                                    }}
                                />
                            ) : null}
                            {/* 展开/收起按钮 */}
                            <div style={toggleButtonStyle} onClick={toggleCollapsed}>
                                {collapsed ? <RightOutlined /> : <LeftOutlined />}
                            </div>
                        </div>
                        <div className={'px-4 py-2'}>
                            总字数：<span className={'font-bold'}>{wordCount}</span>
                        </div>
                    </Sider>
                    <Content
                        style={{
                            marginLeft: mlVal,
                            transition: 'all 0.3s',
                        }}
                    >
                        {!contentDataLoaded ? (
                            <div className="flex h-full flex-col items-center justify-center">
                                <Spin size="large" tip="正在加载..." />
                            </div>
                        ) : (
                            <div className={styles.result}>
                                <div className={styles.resultPage}>
                                    <div className={styles.editorWrapper}>
                                        {contentDataLoaded && (
                                            <AcademicEditor
                                                ref={editorRef}
                                                data={{
                                                    abstract_zh:
                                                        abstractDataInceptionData.abstractCn,
                                                    abstract_en:
                                                        abstractDataInceptionData.abstractEn,
                                                    main_body: contentInceptionData,
                                                }}
                                                onOpenModal={handleOpenModal}
                                                getWordCount={getWordCount}
                                                onSaveSingChapter={onSaveSingChapter}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Content>
                    <Sider
                        width={'104px'}
                        style={{
                            ...siderStyle,
                            right: 0,
                            top: 58,
                            height: 'calc(100vh - 58px)',
                            background: '#fff',
                        }}
                    >
                        <div className={'mb-8 mt-3 flex justify-end'}>
                            <Space direction="vertical">
                                <Button
                                    icon={<SaveOutlined />}
                                    size={'mini'}
                                    onClick={handleSaveContent}
                                >
                                    保存
                                </Button>
                                <Button
                                    size={'mini'}
                                    icon={<DownloadOutlined />}
                                    type={'primary'}
                                    onClick={showOrderModal}
                                >
                                    下载
                                </Button>
                            </Space>
                        </div>
                        <div className="tools rounded bg-[#F8FBFF] p-6">
                            {Tools.map((item) => {
                                return (
                                    <ConditionalWrapper
                                        condition={isDefaultOrderNo}
                                        wrapper={(children) => (
                                            <Tooltip
                                                trigger={'click'}
                                                placement="left"
                                                title={'请下单后使用完整AI功能'}
                                            >
                                                {children}
                                            </Tooltip>
                                        )}
                                    >
                                        <div
                                            className="mb-8 cursor-pointer select-none"
                                            onMouseDown={(e) => {
                                                if (isDefaultOrderNo) return;
                                                handleToolClick(e, item);
                                            }}
                                        >
                                            <img
                                                src={item.icon}
                                                className="mx-auto block h-6 w-6"
                                                alt=""
                                            />
                                            <div className="mt-1 text-center text-sm">
                                                {item.title}
                                            </div>
                                        </div>
                                    </ConditionalWrapper>
                                );
                            })}
                        </div>
                    </Sider>
                </Layout>
            )}
        </>
    );
});
export default Editor;
